import styled from '@emotion/styled';
import type { FunctionComponent } from 'react';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Morph } from '~/ui/animations/morph/Morph';
import { FloatingSplitActionButton } from '~/ui/core/floating-action-button/FloatingActionButton';
import { IconAsterisk, IconCheckmark1Small, IconCrossLarge } from '~/ui/core/icons';
import { ProgressBar } from '~/ui/core/progress-bar/ProgressBar';
import { Spinner } from '~/ui/core/spinner/Spinner';

const NOTIFICATION_DURATION_MS = 10_000;

const AbsoluteProgressBar = styled(ProgressBar)(({ theme }) => ({
    backgroundColor: theme.colors.success[500],
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
}));

const AbsoluteFloatingSplitActionButton = styled(FloatingSplitActionButton)(({ theme }) => ({
    bottom: theme.spacing['4xl'],
    left: '50%',
    position: 'fixed',
    transform: 'translateX(-50%)',
    zIndex: 100,
}));

const animationVariants = {
    hidden: {
        opacity: 0,
        scale: 0.95,
        transform: 'translateX(-50%) translateY(100%)',
        transition: { delay: 1, duration: 0.3 },
    },
    visible: { opacity: 1, scale: 1, transform: 'translateX(-50%) translateY(0)' },
};

type SkillRedetectionNotificationProps = {
    readonly onHide: () => void;
    readonly onRedetectSkills: () => Promise<void>;
};

export const SkillRedetectionNotification: FunctionComponent<SkillRedetectionNotificationProps> = ({
    onHide,
    onRedetectSkills,
}) => {
    const { translate } = useContext(ApplicationContext);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDone, setIsDone] = useState(false);

    useEffect(() => {
        timerRef.current = setTimeout(() => {
            onHide();
        }, NOTIFICATION_DURATION_MS);

        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
            }
        };
    }, [onHide]);

    const handleRedetectClick = async (): Promise<void> => {
        setIsSubmitting(true);
        await onRedetectSkills();
        setIsDone(true);
        onHide();
    };

    const handleCloseClick = (): void => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
        }
        onHide();
    };

    const getAnimationKey = (): string => {
        if (isDone) {
            return 'done';
        }

        return isSubmitting ? 'submitting' : 'idle';
    };

    const getStatusIcon = (): React.ReactNode => {
        if (isDone) {
            return <IconCheckmark1Small size={16} />;
        }

        return isSubmitting ? <Spinner /> : <IconAsterisk size={14} />;
    };

    const getStatusLabel = (): string => {
        if (isDone) {
            return translate('pages.job-architecture-v2.skill-redetection-notification.redetected-skills');
        }

        return isSubmitting
            ? translate('pages.job-architecture-v2.skill-redetection-notification.redetecting-skills')
            : translate('pages.job-architecture-v2.skill-redetection-notification.redetect-skills');
    };

    return (
        <AbsoluteFloatingSplitActionButton
            animate="visible"
            colorVariant="dark"
            exit="hidden"
            initial="hidden"
            leftButton={{
                'aria-label': translate('pages.job-architecture-v2.skill-redetection-notification.redetect-skills'),
                content: (
                    <Fragment>
                        {isSubmitting ? null : <AbsoluteProgressBar duration={NOTIFICATION_DURATION_MS} />}
                        <Morph stateKey={getAnimationKey()}>{getStatusIcon()}</Morph>
                        {getStatusLabel()}
                    </Fragment>
                ),
                onClick: handleRedetectClick,
            }}
            rightButton={
                isSubmitting
                    ? null
                    : {
                          'aria-label': translate('common.close'),
                          content: <IconCrossLarge />,
                          onClick: handleCloseClick,
                      }
            }
            variants={animationVariants}
        />
    );
};
